// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getColor from "@insite/mobius/utilities/getColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { xBaseTheme } from "@rugby/XDL/types";
import { css } from "styled-components";

const labelLeftPadding = 10;
const labelLeftMargin = 4;

const labelBackground = (theme: any, isDisabled = false) => {
    const backgroundColor = isDisabled ? "common.backgroundDisabled" : "common.white";
    return `background: linear-gradient(0deg, 
            transparent 25%, 
            ${resolveColor(backgroundColor, theme)} 25%, 
            ${resolveColor(backgroundColor, theme)} 55%, 
            transparent 55%, 
            transparent 100%);
    `;
};

const labelOnTop = (hasValue: boolean) => css`
    color: ${getColor("primary")};
    top: ${hasValue ? 2 : 0}px;
    ${({ theme }) => labelBackground(theme)}
    transform: translateY(-50%) scale(${hasValue ? 0.8 : 0.9});
    font-weight: bold;
    height: auto;
    width: auto;
    padding: 0 0.3rem;
`;

const labelStyle = css`
    div[class^="DateTimePickerStyle"] ~ label,
    input ~ label,
    div[role="combobox"] ~ label,
    textarea ~ label,
    select ~ label {
        position: absolute;
        font-size: 1rem;
        left: 0;
        color: gray;
        margin: 0 10px 0 ${labelLeftMargin}px;

        // To hide values
        padding-left: ${labelLeftPadding}px;

        transition: 0.1s ease-out;
        transform-origin: left top;
        pointer-events: none;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const labelWidth = (hasIcon: boolean) => {
    const iconSize = 36;
    // padding and margin
    const space = labelLeftMargin + labelLeftPadding;
    const value = hasIcon ? iconSize : space;
    return `
        max-width: calc(100% - ${value}px);
        width: 100%;
    `;
};

export default {
    defaultProps: {
        border: "rectangle",
        sizeVariant: "default",
        cssOverrides: {
            formField: css`
                position: relative;
            `,
            formInputWrapper: css`
                position: relative;

                // To put the label in the middle of the input
                ${labelStyle}

                input:required {
                    box-shadow: none;
                }

                input:invalid {
                    box-shadow: none;
                }

                input ~ label,
                textarea ~ label,
                select ~ label {
                    ${labelWidth(false)};
                }

                div[class^="DateTimePickerStyle"] ~ label,
                div[role="combobox"] ~ label,
                select ~ label {
                    ${labelWidth(true)};
                }

                div[class^="DateTimePickerStyle"] {
                    min-width: 150px;
                }

                div[class^="DateTimePickerStyle"] ~ label,
                select ~ label,
                div[role="combobox"] ~ label,
                input ~ label {
                    top: 50%;
                    transform: translateY(-50%);
                    background: ${({ theme }) => resolveColor("common.white", theme)};
                }

                textarea ~ label {
                    top: 10%;
                    background: ${({ theme }) => resolveColor("common.white", theme)};
                    width: 100%;
                    height: calc(100% - 20px);
                }

                // To put the label in the top of the input
                .select:focus {
                    border: 2px solid ${({ theme }) => resolveColor("primary", theme)};
                }

                div[data-calendar-state^="calendar-open"] ~ label,
                input:focus ~ label,
                textarea:focus ~ label {
                    ${labelOnTop(false)}
                }

                // To put the label in the top of the input when the input has value
                &.has-value label,
                &.has-value div[class^="DateTimePickerStyle"] ~ label,
                &.has-value div[role="combobox"] ~ label,
                &.has-value input[aria-invalid="true"] ~ label {
                    ${labelOnTop(true)}
                }

                // Disabled and Invalid-Input
                // To change the label color to danger

                input[aria-invalid="true"]:focus ~ label,
                &.has-value input[aria-invalid="true"] ~ label,
                div[data-calendar-state^="calendar-open"][aria-invalid="true"] ~ label,
                &.has-value div[aria-invalid="true"] ~ label {
                    color: ${getColor("danger")};
                }

                input:disabled ~ label,
                select:disabled ~ label,
                textarea:disabled ~ label {
                    background: ${getColor("common.backgroundDisabled")};
                }

                &.has-value select:disabled ~ label,
                &.has-value input:disabled ~ label,
                &.has-value textarea:disabled ~ label {
                    ${({ theme }) => labelBackground(theme, true)}
                }

                .mobiusFileUpload {
                    border-width: 2px;
                }

                // To hide select arrow from IE browser
                select::-ms-expand {
                    display: none;
                }

                // added to prevent overlap between text and icon
                &&& {
                    select,
                    select:focus,
                    input[role="searchbox"],
                    input[data-has-icon="true"],
                    input[data-has-icon="true"]:focus {
                        padding-right: 36px;
                    }
                }

                &&& {
                    input,
                    select,
                    textarea {
                        background: ${getColor("common.white")};
                    }
                }

                // To make button has z-index grater than label
                button[class*="FormFieldClickable"] {
                    z-index: 2;
                }

                // <XNG - RUG 587>
                // fix firefox arrow issue
                &&& {
                    input[type="number"]:not(.react-datetime-picker__inputGroup__input) {
                        -moz-appearance: textfield;
                    }

                    input[type="number"]:not(.react-datetime-picker__inputGroup__input):focus {
                        -moz-appearance: none;
                    }
                }
                // </XNG - RUG 587>
            `,
            inputSelect: css`
                background: ${getColor("common.white")};
            `,
        },
        labelProps: {
            size: 15,
            color: "secondary",
            weight: "normal",
        },
    },
} as xBaseTheme["formField"];
